import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import SharedReactTable from "components/shared/reactTable";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { CAN_RESET_DISTRIBUTION_BID_IN_PROCESS_STATUS_KEY } from "constants/localStorage";
import { dateTimeToView } from "helpers/date";
import { roles, userStatesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { BooleanColumnFilter, SelectColumnFilter } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import moment from "moment";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { getOrganizationNames, loginExternal } from "sources/admin";

class AdminUsers extends BookIndex {
    state = {
        isLoading: false,
        tableColumns: this.tableColumns,
    };

    componentWillUnmount() {
        this.props.clearParams();
    }

    get tableColumns() {
        return [
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            { Header: "Эл. почта", accessor: "email", filterable: true },
            { Header: "ФИО", accessor: "fullName", sortKey: "lastName", filterable: true },
            {
                Header: "Телефон",
                accessor: "phoneNumber",
                filterable: true,
                Cell: ({
                    value,
                    row: {
                        original: { phoneNumberExtension },
                    },
                }) => (value ? `${value} ${phoneNumberExtension ? `(+доб. ${phoneNumberExtension})` : ""}` : ""),
            },
            {
                Header: "Организация",
                accessor: "organizationName",
                id: "organizationId",
                sortable: true,
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: getOrganizationNames,
                            inputProps: { name: "organizationId" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({
                    row: {
                        original: { organizationName, organizationId },
                    },
                }) =>
                    organizationName &&
                    organizationId && <Link to={paths.adminOrganizationsShow(organizationId)}>{organizationName}</Link>,
                CSVExport: ({ value }) => value?.replace(/"/g, ""),
            },
            {
                Header: "Дата регистрации",
                accessor: "createdAt",
                filterable: true,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Роли",
                accessor: "roles",
                sortable: false,
                filterable: true,
                filterType: "enum",
                isMulti: true,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            unSearchable: true,
                            options: roles,
                            inputProps: { name: "roles" },
                            components: { ValueContainer },
                        }}
                    />
                ),
                Cell: ({ value }) => (value && value.length ? value.map((el) => enumLabel(roles, el)).join(", ") : ""),
                CSVExport: ({ value }) =>
                    value && value.length ? value.map((el) => enumLabel(roles, el)).join(", ") : "",
            },
            {
                Header: "Удален",
                accessor: "isDeleted",
                width: 150,
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => {
                    const defaultValue = { id: "isDeleted", value: "false", filterType: "boolean" };
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Удаленные", value: "true" },
                        { label: "Не удаленные", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} defaultValue={defaultValue} />;
                },
                Cell: ({ value }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }) => (value ? "Да" : "Нет"),
            },
            {
                Header: "Действие",
                width: 190,
                notExportable: true,
                id: "action",
                sortable: false,
                Cell: ({ row }) => this.userActions(row.original),
            },
        ];
    }

    userActions = (user) => {
        const { roles } = this.props;
        const { state } = user;
        return (
            <div className="d-block">
                {policy("user", { user: { roles } }).isAdminWithLoginByUserAccess() &&
                    (state === userStatesEnum.Banned ? (
                        <div>
                            <Tooltip overlay="Пользователь заблокирован">
                                <div className="inline-btn inline-btn--without-icon inline-btn--disabled">
                                    Войти под пользователем
                                </div>
                            </Tooltip>
                            <br />
                        </div>
                    ) : (
                        <div>
                            <div
                                className="inline-btn inline-btn--without-icon"
                                onClick={this.loginByUser.bind(this, user)}
                            >
                                Войти под пользователем
                            </div>
                            <br />
                        </div>
                    ))}
                <div className="mt-2 inline-btn inline-btn--edit" onClick={this.editUser.bind(this, user)}>
                    Изменить
                </div>
            </div>
        );
    };

    editUser = ({ id }) => {
        return this.props.modal({
            name: "editUser",
            windowMod: "wide",
            title: "Редактирование пользователя",
            mergeParams: this.props.mergeParams,
            id,
        });
    };

    loginByUser = ({ id, email }) => {
        this.setState({ isLoading: true });
        loginExternal({ id, email }).then((response) => {
            this.changeUser(response);
            this.setState({ isLoading: false });
            window.location = "/";
        });
    };

    changeUser = (response) => {
        const { isRoot, isTechSupport, isIntegrationManager, isAccountManager } = this.props.roles;
        const canResetDistributionBidInProcessStatus = policy("user", {
            user: { roles: this.props.roles },
        }).isAdminWithWriteAccess();
        localStorage.setItem(
            "canManageOrganization",
            isRoot || isTechSupport || isIntegrationManager || isAccountManager,
        );
        localStorage.setItem(CAN_RESET_DISTRIBUTION_BID_IN_PROCESS_STATUS_KEY, canResetDistributionBidInProcessStatus);
        localStorage.setItem("isTechSupport", isTechSupport);
        localStorage.setItem("techUserToken", localStorage.getItem("token"));
        localStorage.setItem("techUserRefreshToken", localStorage.getItem("refreshToken"));
        localStorage.setItem("techUserTokenExpiresIn", localStorage.getItem("tokenExpiresIn"));
        localStorage.setItem("techUserCurrentUser", localStorage.getItem("currentUser"));

        const { accessToken, refreshToken, currentUser } = response;
        const { token, expiresIn } = accessToken;

        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("tokenExpiresIn", moment().add(expiresIn, "seconds").toISOString());
        localStorage.setItem("currentUser", JSON.stringify(currentUser));
    };

    renderHead = () => {
        return (
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">Список пользователей</div>
                <div className="btn-toolbar" />
            </div>
        );
    };

    renderListComponent = () => {
        return (
            <SharedReactTable
                {...this.props}
                {...(this.state.isLoading ? { isLoadingList: true } : {})}
                columns={this.state.tableColumns}
            />
        );
    };

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list book-list--nofilter">
                        {this.renderHead()}
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

export default tableContainer(AdminUsers, "allusers");
